<template>
<div class="user-details-container">
    <!-- Refresh button -->
    <div class="refresh-container">
        <a href="#" class="brand w-nav-brand"><img sizes="84.03125px" alt="" :src="logo" loading="lazy" class="image-389" style="height: 45px;"></a>
        <button @click="fetchUserDetails(user ? user.id : $route.params.id)" class="btn-refresh md-btn md-btn-primary">
            <i class="material-icons">refresh</i> Refresh
        </button>
    </div>

    <div v-if="loading" class="loading">
        <div class="md-loader"></div>
        <p>Loading user details...</p>
    </div>

    <div v-else-if="error" class="error md-card md-card-error">
        <i class="material-icons">error</i>
        <p>{{ error }}</p>
    </div>

    <div v-else-if="user" class="main-content">
        <!-- Left Column: User Details and Cart -->
        <div class="left-column">
            <div class="user-card md-card">
                <h2 class="md-card-title">{{AppName}} - User Profile</h2>

                <div class="user-info">
                    <div class="info-row">
                        <span class="label">Name:</span>
                        <span class="value">{{ user.first_name }} {{ user.last_name }}</span>
                    </div>

                    <div class="info-row">
                        <span class="label">Email:</span>
                        <span class="value">{{ user.email }}</span>
                    </div>

                    <div v-if="user.phone" class="info-row">
                        <span class="label">Phone:</span>
                        <span class="value">{{ user.phone }}</span>
                    </div>

                    <div v-if="user.gender" class="info-row">
                        <span class="label">Gender:</span>
                        <span class="value">{{ user.gender }}</span>
                    </div>

                    <div v-if="user.dob" class="info-row">
                        <span class="label">Date of Birth:</span>
                        <span class="value">{{ user.dob }}</span>
                    </div>

                    <div v-if="user.weight" class="info-row">
                        <span class="label">Weight:</span>
                        <span class="value">{{ user.weight }}</span>
                    </div>
                    <div v-if="user.desired_weight" class="info-row">
                        <span class="label">Desired Weight:</span>
                        <span class="value">{{ user.desired_weight }}</span>
                    </div>

                    <div v-if="user.height_inch" class="info-row">
                        <span class="label">Height (inches):</span>
                        <span class="value">{{ user.height_inch }}</span>
                    </div>
                </div>
            </div>

            <!-- Cart Information Section -->
            <div class="cart-section md-card">
                <h3 class="md-card-title">Cart Information</h3>

                <div v-if="cart" class="current-product md-panel">
                    <p><b>Current Product:</b> {{ cart.cart_items.name }}</p>
                    <div class="cart-actions">
                        <button @click="showUpdateCartDialog = true" class="btn-update-cart md-btn md-btn-success">
                            <i class="material-icons">edit</i> Update Cart Product
                        </button>
                        <button @click="sendCartLinkSMS" class="btn-send-link md-btn md-btn-info" :disabled="sendingLink">
                            <i class="material-icons">{{ sendingLink ? 'hourglass_empty' : 'send' }}</i>
                            {{ sendingLink ? 'Sending...' : 'Send Cart Link' }}
                        </button>
                    </div>

                    <!-- SMS Status Message -->
                    <div v-if="smsStatus" class="sms-status" :class="{'sms-success': smsStatus === 'success', 'sms-error': smsStatus === 'error'}">
                        <i class="material-icons">{{ smsStatus === 'success' ? 'check_circle' : 'error' }}</i>
                        <span>{{ smsStatusMessage }}</span>
                    </div>
                </div>

                <!-- If order exists, show order placed message -->
                <div v-if="orders && orders.length > 0" class="order-exists md-alert md-alert-success">
                    <i class="material-icons">check_circle</i>
                    <div>
                        <p><b>Order Already Placed</b></p>
                        <p>This user has completed their order. Cart management is disabled.</p>
                    </div>
                </div>

                <!-- Show cart contents -->
                <div v-else class="current-product md-panel">
                    <h3 class="md-card-subtitle">Charge User</h3>
                    <div class="info-row">
                        <span class="label">Select Product</span>
                        <span class="value">
                            <select v-model="selectedProductId" @change="updateSelectedProduct">
                                <option value="">Choose a product</option>
                                <option value="675ac4cb6bf102c19ec3afec">Semaglutide</option>
                                <option value="675ac4cb6bf102c19ec3afe9">Tirzepatide</option>
                            </select>
                        </span>
                    </div>
                    <div class="info-row">
                        <span class="label">Select Plan</span>
                        <span class="value">
                            <select v-model="selectedPlan" @change="updatePlanAndPrice">
                                <option value="">Choose a plan</option>
                                <option value="1">Monthly</option>
                                <option value="3">Quarterly</option>
                                <option value="6">Semi Annually</option>
                                <option value="12">Annually</option>
                            </select>
                        </span>
                    </div>
                    <div v-if="selectedProductName && selectedPlan" class="info-row">
                        <span class="label">Price:</span>
                        <span class="value">${{ currentPrice - paymentInfo.discount }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">Shipping Address</span>
                        <span class="value">
                            <google-places-autocomplete :api-key="googleMapsApiKey" @address-updated="getShippingAddress" />
                        </span>
                    </div>
                    <div class="info-row">
                        <span class="label">Billing Address</span>
                        <span class="value">
                            <div>
                                <label>
                                    <input type="checkbox" v-model="useSameAddress" @change="copyShippingToBilling">
                                    Same as shipping address
                                </label>
                            </div>
                            <div v-if="!useSameAddress">
                                <google-places-autocomplete :api-key="googleMapsApiKey" @address-updated="getBillingAddress" />
                            </div>
                        </span>
                    </div>
                    <div class="info-row">
                        <span class="label">Enter Card</span>
                        <span class="value">
                            <div id="card-element" class="ns-card-element" ref="cardElement"></div>
                        </span>
                    </div>

                    <!-- Replace the existing coupon section with this updated version -->
                    <!-- Replace your existing coupon input section with this -->
                    <div class="info-row">
                        <span class="label">Enter Coupon</span>
                        <span class="value">
                            <div class="coupon-input-container">
                                <input type="text" v-model="couponCode" class="tf_textfield-3 w-input" style="width:50%; margin-right:10px" :class="{'invalid': couponError}" :disabled="couponApplied">
                                <a href="#" @click.prevent="checkCoupon()" class="md-btn md-btn-primary" style="text-decoration: none; margin-right: 10px;" :class="{'md-btn-success': couponApplied}" :disabled="couponApplied">
                                    {{ couponApplied ? 'Applied' : 'Apply' }}
                                </a>
                                <!-- X button to remove coupon -->
                                <button v-if="couponApplied" @click="removeCoupon" class="remove-coupon-btn" title="Remove Coupon">
                                    <i class="material-icons">close</i>
                                </button>
                            </div>
                        </span>
                    </div>

                    <!-- Success message when coupon is applied -->
                    <div class="info-row" v-if="couponApplied && !couponError">
                        <span class="label" style="color:green;">
                            <i class="material-icons" style="font-size: 16px; vertical-align: middle;">check_circle</i>
                            Coupon Applied
                        </span>
                        <span class="value" style="color:green;">
                            {{ paymentInfo.couponCode }} - ${{ paymentInfo.discount }} off
                        </span>
                    </div>

                    <!-- Error message when coupon is invalid -->
                    <div class="info-row" v-if="couponError">
                        <span class="label" style="color:red;">
                            <i class="material-icons" style="font-size: 16px; vertical-align: middle;">error</i>
                            Invalid Coupon
                        </span>
                        <span class="value" style="color:red;">
                            Please check your coupon code and try again
                        </span>
                    </div>
                    <div class="info-row">
                        <button @click="getStripeToken" :disabled="!canProcessPayment || submitted" class="md-btn md-btn-primary">
                            <i class="material-icons">payment</i>
                            {{ submitted ? 'Processing...' : 'Process Payment' }}
                        </button>
                    </div>

                    <!-- Payment Error Display -->
                    <div v-if="paymentError" class="info-row">
                        <div class="error-message md-alert md-alert-error">
                            <i class="material-icons">error</i>
                            <p>{{ errorMessage }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right Column: Pricing Configuration -->
        <div class="right-column">
            <div class="pricing-panel md-card">
                <h2 class="md-card-title">{{AppName}} - Pricing Configuration</h2>

                <div class="pricing-section">
                    <h3 class="md-card-subtitle">Semaglutide</h3>
                    <table class="md-table">
                        <thead>
                            <tr>
                                <th>Plan</th>
                                <th>Price (USD)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(price, months) in pricingConfig.semaglutide" :key="'s-' + months">
                                <td>{{ planLabels[months] }}</td>
                                <td>${{ price.toFixed(2) }} </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="pricing-section">
                    <h3 class="md-card-subtitle">Tirzepatide</h3>
                    <table class="md-table">
                        <thead>
                            <tr>
                                <th>Plan</th>
                                <th>Price (USD)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(price, months) in pricingConfig.tirzepatide" :key="'t-' + months">
                                <td>{{ planLabels[months] }}</td>
                                <td>${{ price.toFixed(2) }} </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>

    <div v-else class="not-found">
        <p>User not found</p>
    </div>

    <!-- Update Cart Dialog -->
    <div v-if="showUpdateCartDialog" class="dialog-overlay">
        <div class="dialog-content md-dialog">
            <div class="md-dialog-header">
                <h3>Select Product</h3>
                <button @click="cancelUpdateCart" class="md-dialog-close">
                    <i class="material-icons">close</i>
                </button>
            </div>

            <div class="md-dialog-body">
                <div class="product-selection">
                    <div :class="['product-option md-card', { 'md-card-selected': selectedProduct === 'Tirzepatide' }]" @click="selectedProduct = 'Tirzepatide'">
                        <div class="product-icon">
                            <i class="material-icons">medication</i>
                        </div>
                        <p class="product-name">Tirzepatide</p>
                    </div>

                    <div :class="['product-option md-card', { 'md-card-selected': selectedProduct === 'Semaglutide' }]" @click="selectedProduct = 'Semaglutide'">
                        <div class="product-icon">
                            <i class="material-icons">medication</i>
                        </div>
                        <p class="product-name">Semaglutide</p>
                    </div>
                </div>
            </div>

            <div class="md-dialog-footer">
                <button @click="cancelUpdateCart" class="md-btn md-btn-secondary">Cancel</button>
                <button @click="submitUpdateCart" class="md-btn md-btn-primary" :disabled="!selectedProduct">
                    <i class="material-icons">check</i> Submit
                </button>
            </div>
        </div>
    </div>

</div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";

const stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);

import GooglePlacesAutocomplete from './GooglePlacesAutocomplete.vue'

export default {
    name: 'UserDetails',
    components: {
        GooglePlacesAutocomplete
    },
    data() {
        return {
            user: null,
            loading: true,
            error: null,
            cart: null,
            app: process.env.VUE_APP_NAME,
            orders: null,

            // FIXED: Added missing dialog properties that were referenced in template but not defined
            // Reasoning: Vue template was trying to access these properties for v-if directives but they weren't reactive
            showUpdateCartDialog: false,
            couponValid: false, // Tracks if the coupon is valid
            couponApplied: false,
            selectedProduct: null,
            phoneNumber: '',
            submittingCart: false,
            sendingLink: false,
            smsStatus: null, // 'success' or 'error'
            smsStatusMessage: '',
            processing: false,
            submitted: false,
            paymentError: false,
            errorMessage: '',
            paymentAttempts: 0,
            maxPaymentAttempts: 3,
            couponError: false,
            trustUrl: '',
            errorReason: false,
            googleMapsApiKey: process.env.VUE_APP_GOOGLE_KEY,
            // FIXED: Added logo property that was referenced in template but missing from data
            // Reasoning: Template was trying to bind :src="logo" but logo wasn't defined as a reactive property
            logo: process.env.VUE_APP_LOGO,
            AppName: process.env.VUE_APP_NAME,
            cardElement: null,
            selectedProductId: '',
            selectedProductName: '',
            selectedPlan: '',
            currentPrice: 0,
            couponCode: '',
            useSameAddress: true,
            shippingAddress: {
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: '',
                zip: ''
            },
            billingAddress: {
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: '',
                zip: ''
            },
            paymentInfo: {
                payment_terms: 6,
                user_id: '',
                cardToken: '',
                amount: 0,
                discount: 0,
                medication: '',
                couponCode: '',
                drug_mode: '',
                couponId: null,
                paymentMethod: 'card',
                cartItems: [],
                cardDetails: null,
                planId: '',
                // Card info from Basis Theory
                cardBin: '',
                cardLast4: '',
                cardBrand: '',
            },

            // FIXED: Added proper error handling for pricing configuration parsing
            // Reasoning: The original code was trying to parse process.env.PRICING_CONFIG directly which could be undefined
            // Now using helper method to safely parse with fallback values
            pricingConfig: this.initializePricingConfig(),

            // FIXED: Added plan labels initialization with proper error handling  
            // Reasoning: Plan labels are used in template for displaying pricing tables, need to be reactive
            planLabels: this.initializePlanLabels()
        }
    },

    computed: {
        canProcessPayment() {
            return this.selectedProductId &&
                this.selectedPlan &&
                this.shippingAddress.addressLine1 &&
                this.billingAddress.addressLine1 &&
                this.cardElement &&
                !this.submitted &&
                this.currentPrice > 0;
        }
    },

    async created() {
        // Get the user ID from the URL parameter
        const userId = this.$route.params.id;

        // Fetch user details when component is created
        this.fetchUserDetails(userId);
    },

    mounted() {
        // Load Material Icons if not already loaded
        if (!document.querySelector('link[href*="material-icons"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
            document.head.appendChild(link);
        }
    },

    methods: {
        // FIXED: Added helper method to safely initialize pricing configuration
        // Reasoning: The original code was directly parsing process.env.PRICING_CONFIG in data(),
        // but environment variables might not be available or might be undefined, causing JSON parse errors
        initializePricingConfig() {
            try {
                // Check if PRICING_CONFIG environment variable exists and is not undefined
                if (process.env.VUE_APP_PRICING_CONFIG && process.env.VUE_APP_PRICING_CONFIG !== 'undefined') {
                    return JSON.parse(process.env.VUE_APP_PRICING_CONFIG);
                } else {
                    // Fallback configuration if environment variable is missing
                    console.warn('PRICING_CONFIG environment variable not found, using fallback configuration');
                    return {
                        semaglutide: {
                            "1": 249.97,
                            "3": 449.97,
                            "6": 809.95,
                            "12": 1349.97
                        },
                        tirzepatide: {
                            "1": 349.97,
                            "3": 749.97,
                            "6": 1349.82,
                            "12": 2349.88
                        }
                    };
                }
            } catch (error) {
                console.error('Error parsing PRICING_CONFIG:', error);
                // Return fallback configuration on parse error
                return {
                    semaglutide: {
                        "1": 249.97,
                        "3": 449.97,
                        "6": 809.95,
                        "12": 1349.97
                    },
                    tirzepatide: {
                        "1": 349.97,
                        "3": 749.97,
                        "6": 1349.82,
                        "12": 2349.88
                    }
                };
            }
        },

        // FIXED: Added helper method to safely initialize plan labels
        // Reasoning: Plan labels need to be consistent and available for the pricing tables
        initializePlanLabels() {
            return {
                "1": "Monthly",
                "3": "Quarterly",
                "6": "Semi Annual",
                "12": "Annual"
            };
        },

        async handleCouponSubmit() {
            // Reasoning: Centralized coupon handling with proper validation
            this.errorMessage = null;
            if (!this.couponCode) {
                this.errorMessage = 'Please enter a coupon code';
                return;
            }
            this.paymentInfo.couponCode = this.couponCode;
            await this.validateCoupon();
        },

        async validateCoupon() {
            // Reasoning: Server-side coupon validation ensures accurate discount application
            if (this.couponApplied) return;
            this.couponError = false;
            try {
                // Use the existing $http instance instead of $axios for consistency
                const response = await this.$http.post('/validate-coupon', {
                    coupon: this.paymentInfo.couponCode,
                    payment_terms: parseInt(this.selectedPlan)
                });

                if (response.data.status === 'success') {
                    this.couponValid = true;
                    this.couponApplied = true;
                    this.paymentInfo.couponId = response.data.coupon_id;
                    this.paymentInfo.discount = response.data.c_val;
                } else {
                    this.couponValid = false;
                    this.couponError = true;
                }
            } catch (error) {
                this.couponValid = false;
                this.couponError = true;
            }
        },

        checkCoupon() {
            // Reasoning: Wrapper method to maintain compatibility with existing template calls
            this.handleCouponSubmit();
        },

        async createCardElement() {
            if (stripe && this.$refs.cardElement) {
                try {
                    const elements = stripe.elements();
                    this.cardElement = elements.create('card');
                    this.cardElement.mount('#card-element');
                    console.log('Card element mounted successfully');

                    this.cardElement.on("change", (event) => {
                        if (event.brand) {
                            this.paymentInfo.cardBrand = event.brand;
                        }
                    });
                } catch (error) {
                    console.error('Error mounting card element:', error);
                }
            }
        },

        updateSelectedProduct() {
            this.resetCouponOnSelectionChange(); // ADD THIS LINE
            
            // Keep all your existing code here
            if (this.selectedProductId === '675ac4cb6bf102c19ec3afec') {
                this.selectedProductName = 'semaglutide';
            } else if (this.selectedProductId === '675ac4cb6bf102c19ec3afe9') {
                this.selectedProductName = 'tirzepatide';
            }
            this.updatePlanAndPrice();
        },

        updatePlanAndPrice() {
            if (this.selectedPlan) {
                this.resetCouponOnSelectionChange(); // ADD THIS BLOCK
            }
            
            // Keep all your existing code here
            if (this.selectedProductName && this.selectedPlan) {
                this.currentPrice = this.pricingConfig[this.selectedProductName][this.selectedPlan];
            }
        },

        async fetchUserDetails(userId) {
            // Reasoning: Set loading state to provide user feedback during API calls
            this.loading = true;
            this.error = null;

            try {
                const response = await this.$http.get(`/users/${userId}`);
                this.user = response.data.user;
                this.cart = response.data.cart;
                this.orders = response.data.orders;

                // Initialize phone number if available
                // Reasoning: Pre-populate phone field to improve user experience
                if (this.user && this.user.phone) {
                    this.phoneNumber = this.user.phone;
                }

                // Show refresh notification if this is not the initial load
                // Reasoning: Provide feedback when user manually refreshes data
                if (!this.loading) {
                    this.$notify({
                        type: 'info',
                        text: 'User data refreshed successfully'
                    });
                }

                // Create card element after DOM is updated
                // Reasoning: Ensure DOM is ready before mounting payment card element
                await this.$nextTick();
                setTimeout(() => {
                    this.createCardElement();
                }, 100);
            } catch (err) {
                console.error('Error fetching user details:', err);
                this.error = 'Failed to load user details. Please try again later.';
            } finally {
                this.loading = false;
            }
        },

        isPOBox(address) {
            // Reasoning: Validate shipping addresses to prevent delivery issues
            // PO Boxes cannot receive certain types of shipments
            const normalized = address.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
            const poBoxRegex = /\b(po box|post office box|p o box)\b(?:\s*\d{0,6})?/i;
            return poBoxRegex.test(normalized);
        },

        getShippingAddress(address) {
            // Reasoning: Validate shipping address to prevent delivery failures
            if (this.isPOBox(address.street)) {
                this.$notify({
                    type: 'error',
                    text: 'PO Box addresses are not allowed for shipping'
                });
                return;
            }

            // Reasoning: Structure address data consistently for backend processing
            this.shippingAddress = {
                addressLine1: address.street,
                addressLine2: address.address_line2 || '',
                city: address.city,
                state: address.state,
                zip: address.zip
            };

            // Reasoning: Auto-copy to billing if user selected same address option
            if (this.useSameAddress) {
                this.copyShippingToBilling();
            }
        },

        getBillingAddress(address) {
            // Reasoning: Structure billing address data for payment processing
            this.billingAddress = {
                addressLine1: address.street,
                addressLine2: address.address_line2 || '',
                city: address.city,
                state: address.state,
                zip: address.zip
            };
        },

        copyShippingToBilling() {
            // Reasoning: Reduce user input when billing and shipping addresses are the same
            if (this.useSameAddress) {
                this.billingAddress = {
                    ...this.shippingAddress
                };
            }
        },

        getStripeToken() {
            // Reasoning: Prevent multiple payment submissions and validate required fields
            this.submitted = true;
            this.paymentError = false;

            if (!this.validateAddress(this.shippingAddress)) {
                this.paymentError = true;
                this.errorMessage = "Please enter a complete shipping address.";
                this.submitted = false;
                return false;
            }

            if (!this.useSameAddress && !this.validateAddress(this.billingAddress)) {
                this.paymentError = true;
                this.errorMessage = "Please enter a complete billing address.";
                this.submitted = false;
                return false;
            }

            this.errorReason = false;
            // Tokenize card details via Stripe
            stripe.createToken(this.cardElement)
                .then((result) => {
                    if (result.error) {
                        this.paymentError = true;
                        this.errorMessage = result.error.message || "Invalid card details";
                        this.submitted = false;
                        return;
                    }
                    const token = result.token;
                    this.paymentInfo.cardToken = token.id;
                    this.paymentInfo.cardDetails = {
                        number: 'XXXXXXXXXXX' + token.card.last4,
                        expiration_month: token.card.exp_month,
                        expiration_year: token.card.exp_year,
                        cardBrand: token.card.brand,
                        cardBin: token.card.brand,
                        cardLast4: token.card.last4,
                    };
                    this.paymentInfo.cardBin = token.card.brand;
                    this.paymentInfo.cardLast4 = token.card.last4;
                    this.paymentInfo.paymentMethod = 'card';
                    this.processPayment();
                })
                .catch((error) => {
                    console.log(error);
                    this.paymentError = true;
                    this.errorMessage = "Invalid card details";
                    this.submitted = false;
                });
        },

        validateAddress(address) {
            // Reasoning: Ensure all required address fields are present for successful delivery/billing
            return address &&
                address.addressLine1 &&
                address.city &&
                address.state &&
                address.zip;
        },
        async blockCart(userId){
            try{
                return await this.$http.post('/call-center/block-card', {userId : userId});
            }catch(error){
                console.log(error)
            }
        },
        async processPayment() {
            try {
                
                // Prepare payment info
                // Reasoning: Structure payment data according to backend API expectations
                this.paymentInfo.user_id = this.user.id;
                this.paymentInfo.id = this.user.id;
                this.paymentInfo.planId = this.selectedProductId;
                this.paymentInfo.medication = this.selectedProductName;
                this.paymentInfo.payment_terms = parseInt(this.selectedPlan);
                this.paymentInfo.couponCode = this.couponCode;
                this.paymentInfo.cartItems = [{
                    id: this.selectedProductId,
                    quantity: 1,
                    price: this.currentPrice,
                    name: this.selectedProductName,
                    retail_price: this.currentPrice
                }];

                // Reasoning: Map product IDs to display names for better user experience
                let productDisplayName = '';
                if (this.selectedProductId === '675ac4cb6bf102c19ec3afec') {
                    productDisplayName = 'Semaglutide';
                } else if (this.selectedProductId === '675ac4cb6bf102c19ec3afe9') {
                    productDisplayName = 'Tirzepatide';
                }

                // Reasoning: Consolidate all payment data into single object for API call
                let paymentData = {
                    ...this.paymentInfo,
                    amount: this.currentPrice,
                    cartTotal: this.currentPrice,
                    cartTotalNoDiscount: this.currentPrice,
                    billingAddress: this.billingAddress,
                    shippingAddress: this.shippingAddress,
                    discount: 0,
                    skipAuth: false,
                    user_name: `${this.user.first_name} ${this.user.last_name}`,
                    product_name: productDisplayName,
                    phone: `${this.user.phone}`,
                };

                const response = await this.sendRequest(paymentData);
                console.log(response)

                // Reasoning: Handle successful payment by refreshing data and resetting form
                if (response.data.status === 'success') {
                    window.location.reload();
                    this.submitted = false;
                } else {
                    this.paymentError = true;
                    this.errorMessage = response.data.message;
                    this.submitted = false;
                }
            } catch (error) {
                // Reasoning: Track payment attempts and provide user feedback on errors
                this.paymentAttempts++;
                this.paymentError = true;
                this.errorMessage = error.response?.data?.message || error.message || "";
                this.submitted = false;
                if (this.paymentAttempts >= 3) {
                    await this.blockCart(this.user.id);
                }
            }
        },
        // Add these two new methods
        removeCoupon() {
            this.couponCode = '';
            this.couponApplied = false;
            this.couponValid = false;
            this.couponError = false;
            this.paymentInfo.couponCode = '';
            this.paymentInfo.couponId = null;
            this.paymentInfo.discount = 0;
            
            this.$notify({
                type: 'info',
                text: 'Coupon removed successfully'
            });
        },

        resetCouponOnSelectionChange() {
            if (this.couponApplied || this.couponCode) {
                this.couponCode = '';
                this.couponApplied = false;
                this.couponValid = false;
                this.couponError = false;
                this.paymentInfo.couponCode = '';
                this.paymentInfo.couponId = null;
                this.paymentInfo.discount = 0;
                
                this.$notify({
                    type: 'warning',
                    text: 'Coupon reset due to medication/plan change. Please re-apply if needed.'
                });
            }
        },
        async sendRequest(paymentData) {
            // Send to your AdonisJS checkout endpoint
            // Reasoning: Centralize API call logic for easier maintenance and error handling
            return await this.$http.post('/call-center/checkout-stripe', paymentData);
        },

        async saveTrustFrom(trustUrl) {
            // Implement trust form saving if needed
            // Reasoning: Placeholder for future trust form functionality
            try {
                // Your trust form saving logic here
                console.log('Saving trust form:', trustUrl);
            } catch (error) {
                console.error('Error saving trust form:', error);
            }
        },

        async updateAddress() {
            // Update user address if needed
            // Reasoning: Placeholder for future address update functionality
            try {
                // Your address update logic here
                console.log('Updating address');
            } catch (error) {
                console.error('Error updating address:', error);
            }
        },

        async clearCart() {
            // Clear cart after successful payment
            // Reasoning: Clean up cart data after order completion
            try {
                if (this.cart && this.cart.id) {
                    await this.$http.post('/clear-cart', {
                        cartId: this.cart.id
                    });
                }
            } catch (error) {
                console.error('Error clearing cart:', error);
            }
        },

        pushToRoute(amountCharged) {
            // Navigate to success page
            // Reasoning: Provide user feedback and update UI after successful payment
            this.$notify({
                type: 'success',
                text: `Payment processed successfully! Amount charged: ${amountCharged}`
            });

            // Refresh user details to show updated order status
            this.fetchUserDetails(this.user.id);

            // You might want to navigate to a success page
            // this.$router.push('/payment-success');
        },

        goBack() {
            // Reasoning: Provide navigation option to return to previous page
            this.$router.go(-1);
        },

        editUser() {
            // Reasoning: Navigate to user edit page when user data needs modification
            if (this.user && this.user.id) {
                this.$router.push(`/users/${this.user.id}/edit`);
            }
        },

        // Cart Update Methods
        cancelUpdateCart() {
            // Reasoning: Reset dialog state when user cancels cart update
            this.showUpdateCartDialog = false;
            this.selectedProduct = null;
        },

        async submitUpdateCart() {
            // Reasoning: Validate selection before making API call
            if (!this.selectedProduct) return;

            this.submittingCart = true;

            try {
                // If cart doesn't exist, use "no_product_found" as cartId
                // Reasoning: Handle cases where user doesn't have an existing cart
                const cartId = this.cart && this.cart.id ? this.cart.id : "no_product_found";

                await this.$http.post('/update-cart', {
                    cartId: cartId,
                    productName: this.selectedProduct,
                    userId: this.user.id // Send userId to help backend create cart if needed
                });

                // Refresh user details to show updated cart
                // Reasoning: Ensure UI reflects the updated cart data
                await this.fetchUserDetails(this.user.id);

                // Show success notification
                this.$notify({
                    type: 'success',
                    text: `Cart updated successfully to ${this.selectedProduct}`
                });
            } catch (error) {
                console.error('Error updating cart:', error);
                this.$notify({
                    type: 'error',
                    text: 'Failed to update cart. Please try again.'
                });
            } finally {
                // Reasoning: Reset form state regardless of success or failure
                this.submittingCart = false;
                this.showUpdateCartDialog = false;
                this.selectedProduct = null;
            }
        },

        // Send Cart Link SMS directly
        async sendCartLinkSMS() {
            // Reasoning: Validate required fields before making API call
            if (!this.user || !this.user.id || !this.phoneNumber) {
                this.smsStatus = 'error';
                this.smsStatusMessage = 'Phone number is required to send SMS';
                return;
            }

            // Reset status and start sending
            this.smsStatus = null;
            this.smsStatusMessage = '';
            this.sendingLink = true;

            try {
                // If cart doesn't exist, use "no_product_found" as cartId
                // Reasoning: Handle cases where user doesn't have an existing cart
                const cartId = this.cart && this.cart.id ? this.cart.id : "no_product_found";

                // Predefined message template
                const firstName = this.user.first_name || 'there';
                const message = `Hi ${firstName}!\nHere's the link to finish your signup:\nSmall favor — please use this link so I get credit on my end and keep my manager happy 😊 I'd really appreciate it!`;

                await this.$http.post('/call-center/send-cart-link', {
                    cartId: cartId,
                    identifire: Date.now().toString(), // Unique identifier
                    message: message,
                    phone: this.phoneNumber,
                    userId: this.user.id
                });

                // Show success message below button
                this.smsStatus = 'success';
                this.smsStatusMessage = 'Message sent successfully!';
            } catch (error) {
                console.error('Error sending cart link:', error);
                this.smsStatus = 'error';
                this.smsStatusMessage = 'Failed to send message. Please try again.';
            } finally {
                // Reasoning: Reset sending state
                this.sendingLink = false;
            }
        }
    }
}
</script>

<style scoped>
/* Material Design inspired styles */
.user-details-container {
    max-width: 1400px;
    /* Increased max-width to utilize more screen space */
    margin: 0 auto;
    padding: 20px;
    position: relative;
    font-family: 'Roboto', Arial, sans-serif;
}

.refresh-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.btn-refresh {
    display: flex;
    align-items: center;
}

/* Main Content Layout - Two Column Grid */
.main-content {
    display: grid;
    grid-template-columns: 1fr 400px;
    /* Left column flexible, right column fixed 400px */
    gap: 30px;
    align-items: start;
}

/* Left Column Styles */
.left-column {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* Right Column Styles */
.right-column {
    position: sticky;
    top: 20px;
    /* Keeps pricing panel visible when scrolling */
}

.pricing-panel {
    max-height: calc(100vh - 100px);
    overflow-y: auto;
}

.pricing-section {
    margin-bottom: 30px;
}

.pricing-section:last-child {
    margin-bottom: 0;
}

.pricing-note {
    font-size: 0.8em;
    color: #666;
    font-style: italic;
}

/* Responsive Design */
@media (max-width: 1200px) {
    .main-content {
        grid-template-columns: 1fr 350px;
        gap: 20px;
    }

    .user-details-container {
        max-width: 1200px;
    }
}

@media (max-width: 992px) {
    .main-content {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    .right-column {
        position: static;
    }

    .user-details-container {
        max-width: 800px;
    }
}

@media (max-width: 768px) {
    .user-details-container {
        padding: 15px;
    }

    .refresh-container {
        flex-direction: column;
        gap: 15px;
        align-items: stretch;
    }

    .main-content {
        gap: 15px;
    }
}

.loading,
.error,
.not-found {
    text-align: center;
    padding: 20px;
}

.md-loader {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top: 4px solid #3498db;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.invalid {
    border-color: red !important;
}

/* Card Styles */
.md-card {
    background-color: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin-bottom: 0;
    /* Remove bottom margin since we're using gap in grid */
    transition: box-shadow 0.3s ease;
}

.md-card:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.md-card-title {
    font-size: 1.5rem;
    font-weight: 500;
    color: #333;
    margin-top: 0;
    margin-bottom: 24px;
    text-align: center;
}

.md-card-subtitle {
    font-size: 1.2rem;
    font-weight: 500;
    color: #555;
    margin-top: 0;
    margin-bottom: 16px;
}

.md-card-section {
    border-top: 1px solid #eee;
    padding-top: 20px;
    margin-top: 20px;
}

.md-card-error {
    background-color: #ffebee;
    color: #d32f2f;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.md-card-selected {
    border: 2px solid #2196F3;
    background-color: #E3F2FD;
}

/* Alert Styles */
.md-alert {
    padding: 16px;
    border-radius: 4px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
}

.md-alert i {
    margin-right: 12px;
    font-size: 24px;
}

.md-alert-success {
    background-color: #E8F5E9;
    color: #2E7D32;
    border-left: 4px solid #4CAF50;
}

.md-alert-error {
    background-color: #ffebee;
    color: #d32f2f;
    border-left: 4px solid #f44336;
}

/* Panel Styles */
.md-panel {
    background-color: #f5f5f5;
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 16px;
}

.md-panel-warning {
    background-color: #FFF8E1;
    border-left: 4px solid #FFC107;
}

.user-info {
    margin-bottom: 20px;
}

.info-row {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid #eee;
    align-items: flex-start;
}

.info-row:last-child {
    border-bottom: none;
}

.label {
    font-weight: 500;
    min-width: 150px;
    width: 150px;
    color: #555;
    flex-shrink: 0;
}

.value {
    flex: 1;
    color: #333;
    word-break: break-word;
}

.actions {
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
}

/* Button Styles */
.md-btn {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: background-color 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    text-decoration: none;
}

.md-btn i {
    margin-right: 6px;
}

.md-btn:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.md-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
}

.md-btn-primary {
    background-color: #2196F3;
    color: white;
}

.md-btn-secondary {
    background-color: #757575;
    color: white;
}

.md-btn-success {
    background-color: #4CAF50;
    color: white;
}

.md-btn-info {
    background-color: #00BCD4;
    color: white;
}

/* Cart Actions */
.cart-actions {
    display: flex;
    justify-content: left;
    margin-top: 15px;
    gap: 30px;
}

@media (max-width: 600px) {
    .cart-actions {
        flex-direction: column;
    }
}

/* SMS Status Message */
.sms-status {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 15px;
    padding: 12px 16px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
}

.sms-status i {
    font-size: 20px;
}

.sms-success {
    background-color: #E8F5E9;
    color: #2E7D32;
    border-left: 4px solid #4CAF50;
}

.sms-error {
    background-color: #ffebee;
    color: #d32f2f;
    border-left: 4px solid #f44336;
}

/* Dialog Styles */
.dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.md-dialog {
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    max-height: 90vh;
    overflow: hidden;
}

.md-dialog-header {
    padding: 20px 24px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.md-dialog-header h3 {
    margin: 0;
    font-weight: 500;
    font-size: 1.2rem;
    color: #333;
}

.md-dialog-close {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #757575;
    padding: 4px;
    border-radius: 50%;
    display: flex;
}

.md-dialog-close:hover {
    background-color: #f1f1f1;
}

.md-dialog-body {
    padding: 24px;
    overflow-y: auto;
}

.md-dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 24px;
    border-top: 1px solid #eee;
}

/* Product Selection */
.product-selection {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
}

.product-option {
    flex: 1;
    padding: 20px;
    cursor: pointer;
    text-align: center;
    transition: all 0.2s;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.product-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    background-color: #E3F2FD;
    border-radius: 50%;
    margin-bottom: 12px;
}

.product-icon i {
    font-size: 30px;
    color: #2196F3;
}

.product-name {
    font-weight: 500;
    margin-bottom: 8px;
    color: #333;
    font-size: 1.1rem;
}

.product-price {
    color: #4CAF50;
    font-weight: 500;
    font-size: 1.2rem;
}

/* Form Styles */
.md-form-group {
    margin-bottom: 20px;
}

.md-form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #555;
    font-size: 0.9rem;
}

.md-input-container {
    position: relative;
}

.md-input-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #757575;
}

.md-input,
.md-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
}

.md-input {
    padding-left: 40px;
}

.md-input:focus,
.md-textarea:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

.md-textarea {
    resize: vertical;
    min-height: 80px;
}

/* Order message */
.order-exists {
    display: flex;
    align-items: flex-start;
}

.order-exists i {
    font-size: 24px;
    margin-right: 12px;
    margin-top: 2px;
}

select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    padding: 10px 40px 10px 12px;
    border: 1px solid #ccc;
    border-radius: 8px;
    background-color: #fff;
    background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23666' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 12px 8px;
    font-size: 16px;
    color: #333;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    width: 100%;
    box-sizing: border-box;
}

select:focus {
    border-color: #66afe9;
    box-shadow: 0 0 4px rgba(102, 175, 233, 0.6);
    outline: none;
}

.tf_textfield-3.w-input {
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 16px;
    box-sizing: border-box;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.tf_textfield-3.w-input:focus {
    border-color: #66afe9;
    box-shadow: 0 0 4px rgba(102, 175, 233, 0.6);
    outline: none;
}

.md-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-family: 'Roboto', sans-serif;
}

.md-table th,
.md-table td {
    border-bottom: 1px solid #e0e0e0;
    padding: 12px 16px;
    text-align: left;
}

.md-table th {
    background-color: #f5f5f5;
    color: #333;
    font-weight: 500;
}

.md-table tr:hover {
    background-color: #f9f9f9;
}

/* Card element styles for payment */
.ns-card-element {
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 6px;
    background-color: #fff;
}

/* Additional responsive improvements */
@media (max-width: 480px) {
    .md-card {
        padding: 16px;
    }

    .info-row {
        flex-direction: column;
        gap: 5px;
    }

    .label {
        min-width: auto;
        width: auto;
        font-weight: 600;
    }

    .md-table th,
    .md-table td {
        padding: 8px 12px;
        font-size: 14px;
    }

    .product-selection {
        flex-direction: column;
        gap: 10px;
    }
}

.current-product {
    margin-bottom: 16px;
}

.current-product:last-child {
    margin-bottom: 0;
}

/* Error message styles */
.error-message {
    width: 100%;
    margin-top: 10px;
}

.error-message p {
    margin: 0;
}

.coupon-input-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
}

.remove-coupon-btn {
    background-color: #f44336;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s ease;
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.remove-coupon-btn:hover {
    background-color: #d32f2f;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.remove-coupon-btn i {
    font-size: 18px;
    margin: 0;
}
</style>
